import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import {
  Spinner,
  Button,
  Badge,
  Checkbox,
  Dropdown,
  Option,
  Textarea,
  Input,
  Tab,
  TabList,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Tooltip,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  tokens,
} from '@fluentui/react-components';
import {
  MailRegular,
  SendRegular,
  PersonRegular,
  BuildingRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  HistoryRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  EyeRegular,
  DocumentPdfRegular,
  ArrowSyncRegular,
  StarRegular,
  PhoneRegular,
  OpenRegular,
  SearchRegular,
  BookmarkRegular,
  StarFilled,
  DeleteRegular,
  SaveRegular,
  ColumnTripleRegular,
} from '@fluentui/react-icons';
import { format } from 'date-fns';
import DataTable, { type Column } from '../components/DataTable';
import { TableSkeleton } from '../components/LoadingSkeleton';
import {
  getEligibleClients,
  getOutreachHistory,
  getEmailTemplates,
  sendOutreach,
  getDrakeChecklists,
  resolveBucketContacts,
  getPreparerLastSnapshot,
  getPreparerForYear,
  type EligibleClient,
  type ClientOutreach,
  type EmailTemplate,
  type DrakeChecklist,
  type PreparerHistorySnapshot,
} from '../services/api';
import type { TaxReturn } from '../types';
import SendOutreachDialog from '../components/outreach/SendOutreachDialog';
import PreparerRosterDialog from '../components/outreach/PreparerRosterDialog';
import {
  useSavedViews,
  useCreateSavedView,
  useDeleteSavedView,
  useSetSavedViewDefault,
  CRM_VIEWS_APP_ID,
  type SavedViewDto,
} from '../services/crmViewsApi';
import { useScreenStateDefault } from '../hooks/useScreenStateDefault';
import { decodeQueryState, encodeQueryState, splitStaleFilters } from '../utils/screenStateQuery';
import { useNotificationToast } from '../hooks/useNotificationToast';
import { getDataverseRecordUrl, isDataverseConfigured } from '../utils/dataverse';
import { useOutreachScopeParams } from './useOutreachScopeParams';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { applyClientFilters } from '../components/listFilters/applyClientFilters';
import { computeFacetCounts, computeNoValueCount } from '../components/listFilters/facetCounts';
import { NO_VALUE_TOKEN } from '../components/listFilters/columnFilterOps';
import { ColumnHeaderFilter } from '../components/listFilters/ColumnHeaderFilter';
import { ColumnMenu } from '../components/listFilters/ColumnMenu';
import { isFilterPanelOpen } from '../components/listFilters/openPanelRegistry';
import { ActiveFiltersChips } from '../components/listFilters/ActiveFiltersChips';
import { QuickViewPills, type QuickView } from '../components/listFilters/QuickViewPills';
import type { FilterColumn } from '../components/listFilters/types';

const currentYear = new Date().getFullYear();
const defaultTaxYear = currentYear - 1;

// Wave 2A step 9 (lowest priority, optional-conformance per the plan doc): the column chooser,
// ported directly from TaxReturnsPage.tsx's own `loadColumnOverrides`/`toggleColumn` pattern
// (§13). 'select' and 'actions' are always shown, same as TaxReturnsPage's own '_select'/
// '_actions' convention -- not offered as togglable.
//
// Wave 4 (2026-11-03): the column menu's explicit "Sort A -> Z"/"Sort Z -> A" (M1) is now wired
// here too, via `useOutreachScopeParams`'s own `sort`/`setSort` (grown onto that hook rather than
// a second `useSearchParams()` instance -- see its doc comment). Group-by is correctly OMITTED on
// this page (C66): `filterColumns` below has no `filterType: 'date-range'` column at all
// (`lastFiledYear` is a number) -- confirmed by grep before deciding this, not assumed. Only
// TaxReturnsPage's `scheduledDate` column groups.
const OUTREACH_COLUMN_KEYS = ['name', 'clientType', 'lastFiledYear', 'preferredPreparerName', 'lastPreparerName', 'alreadyContacted'] as const;
const OUTREACH_COL_STORAGE_KEY = 'outreach-columns';
// Hoisted out of JSX for the same reason `clientColumns` is memoized above: a fresh array
// literal every render is a new reference every render, and this feeds the same
// onPageDataChange-wired DataTable.
const OUTREACH_CLIENT_SEARCH_KEYS: (keyof EligibleClient)[] = ['name', 'email', 'phone'];

function loadOutreachColumnOverrides(): string[] {
  try {
    const raw = localStorage.getItem(OUTREACH_COL_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch { /* ignore */ }
  return [...OUTREACH_COLUMN_KEYS];
}

export default function OutreachPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const preselectedClientId = searchParams.get('clientId');
  const preselectedBucket = searchParams.get('bucket');
  const preselectedReturnType = searchParams.get('returnType');
  const {
    tab: selectedTab,
    pageSize,
    page: currentPage,
    q: searchText,
    filters,
    sort,
    setTab: setSelectedTab,
    setPageSize,
    setPage: setCurrentPage,
    setQ: setSearchText,
    setFilters,
    setSort,
    applyRemembered,
  } = useOutreachScopeParams();
  // §16.5's page-level keyboard half: `/` focuses the search box (unless already typing in an
  // editable field) and Escape closes an open filter surface FIRST -- only a second Escape, with
  // no surface open, clears `q`. See the keydown effect below and isFilterPanelOpen's own doc
  // comment for why the Escape listener must be registered with `capture: true`.
  const searchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    function isEditableTarget(target: EventTarget | null): boolean {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        // §16.5: a filter panel still open at this instant (checked BEFORE its own Escape
        // handler runs, via capture-phase ordering) means THIS Escape is the one closing it --
        // isFilterPanelOpen's own doc comment explains why capture is what makes this ordering
        // observable. Only a later Escape, with nothing open, clears q. And this is scoped to
        // the page's OWN filter surface only -- an open Fluent Dialog (send/preview/roster/save-
        // view) handles its own Escape-to-close; this handler must not also clear q underneath it.
        if (isFilterPanelOpen()) return;
        if (document.querySelector('[role="dialog"]')) return;
        if (searchText) setSearchText('');
        return;
      }
      if (e.key === '/' && !isEditableTarget(document.activeElement) && !document.querySelector('[role="dialog"]')) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [searchText, setSearchText]);
  const [selectedClients, setSelectedClients] = useState<Set<string>>(new Set());
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('tax_season_kickoff');
  const [customSubject, setCustomSubject] = useState('');
  const [customBody, setCustomBody] = useState('');
  const [sendResult, setSendResult] = useState<{
    success: number;
    failed: number;
    errors: string[];
  } | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [historyPreviewOpen, setHistoryPreviewOpen] = useState(false);
  const [selectedOutreach, setSelectedOutreach] = useState<ClientOutreach | null>(null);
  const [enhancedSendDialogOpen, setEnhancedSendDialogOpen] = useState(false);
  const [currentPageClients, setCurrentPageClients] = useState<EligibleClient[]>([]);
  // Wave 2A (Client Outreach as the fleet filter-contract v3 reference, 2026-11-01): `filters` now
  // lives in the URL (steps 7/8, 2026-11-02) via useOutreachScopeParams, which grew its own
  // filter.*-param ownership instead of running a second useListSearchParams instance on this
  // page's URL (the two-hook race this comment used to name as a named follow-up) -- see that
  // hook's own doc comment.
  const { preparerId: currentUserId } = useCurrentUser();

  // Fetch eligible clients — staleTime: 0 so opt-outs are always reflected on mount
  const { data: clients, isLoading: clientsLoading, error: clientsError, refetch: refetchClients } = useQuery<EligibleClient[]>({
    queryKey: ['eligibleClients'],
    queryFn: () => getEligibleClients(2),
    staleTime: 0,
  });

  // Resolve bucket contacts when arriving from PriorityOutreachPage's "Create campaign" deep link
  const bucketContactsQuery = useQuery({
    queryKey: ['outreach-bucket-contacts', preselectedBucket, preselectedReturnType],
    queryFn: () => preselectedBucket
      ? resolveBucketContacts(preselectedBucket, 'email', 'tax_season_kickoff', preselectedReturnType ?? undefined)
      : Promise.resolve({ contactIds: [] }),
    enabled: !!preselectedBucket,
  });

  // Fetch outreach history — staleTime: 0 so recently sent outreach is always fresh
  const { data: history, isLoading: historyLoading, error: historyError, refetch: refetchHistory } = useQuery<ClientOutreach[]>({
    queryKey: ['outreachHistory', defaultTaxYear],
    queryFn: () => getOutreachHistory(defaultTaxYear),
    retry: 2,
    retryDelay: 1000,
    staleTime: 0,
  });

  // Fetch email templates
  const { data: templates } = useQuery<EmailTemplate[]>({
    queryKey: ['emailTemplates'],
    queryFn: () => getEmailTemplates(),
  });

  // Fetch Drake checklists for current tax year (to show personalized indicator)
  const { data: drakeChecklists } = useQuery<DrakeChecklist[]>({
    queryKey: ['drakeChecklists', defaultTaxYear + 1],
    queryFn: () => getDrakeChecklists(defaultTaxYear + 1, 'matched'),
  });

  // Create a map of clientId -> Drake checklist for quick lookup
  const clientDrakeMap = useMemo(() => {
    const map = new Map<string, DrakeChecklist>();
    if (drakeChecklists) {
      for (const checklist of drakeChecklists) {
        if (checklist.clientId) {
          map.set(checklist.clientId, checklist);
        }
      }
    }
    return map;
  }, [drakeChecklists]);

  // Pre-select client if clientId is in URL params
  useEffect(() => {
    if (preselectedClientId && clients) {
      const client = clients.find(c => c.id === preselectedClientId);
      if (client) {
        setSelectedClients(new Set([client.id]));
      }
    }
  }, [preselectedClientId, clients]);

  // Pre-select bucket contacts when arriving from PriorityOutreachPage's "Create campaign" deep link
  useEffect(() => {
    if (bucketContactsQuery.data?.contactIds?.length) {
      setSelectedClients(new Set(bucketContactsQuery.data.contactIds));
    }
  }, [bucketContactsQuery.data]);

  // Wave 2A step 6 (required preview before any send, core's binding rule): the preview FREEZES
  // the id set the moment it opens; every send path downstream of a preview uses exactly this
  // frozen list, never a re-resolved `selectedClients`, even if the matching set changes between
  // preview and send (a client opts out, a filter result refetches, etc.).
  const [frozenSendIds, setFrozenSendIds] = useState<string[]>([]);
  // Which send path the preview was opened FOR -- null means the standalone "Preview" trigger
  // (no forced next step; defaults to the plain email path in the dialog's own action button).
  const [pendingSendPath, setPendingSendPath] = useState<'email' | 'emailSms' | null>(null);
  const frozenClientsList = useMemo(() => {
    if (!clients || frozenSendIds.length === 0) return [];
    const frozen = new Set(frozenSendIds);
    return clients.filter(c => frozen.has(c.id));
  }, [clients, frozenSendIds]);
  const openPreviewFor = useCallback((path: 'email' | 'emailSms' | null) => {
    setFrozenSendIds(Array.from(selectedClients));
    setPendingSendPath(path);
    setPreviewIndex(0);
    setPreviewDialogOpen(true);
  }, [selectedClients]);

  const currentPreviewClient = frozenClientsList[previewIndex];

  // Generate preview email HTML
  const generatePreviewEmail = (clientName: string): string => {
    const year = defaultTaxYear + 1;

    if (selectedTemplate === 'custom' && customBody) {
      return `
        <div style="font-family: 'Segoe UI', Arial, sans-serif; line-height: 1.6; color: #333;">
          <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
            <div style="background: #0078d4; color: white; padding: 20px; text-align: center;">
              <h2 style="margin: 0;">PTC Financial</h2>
            </div>
            <div style="padding: 20px; background: var(--tl-bg-subtle);">
              <p>Dear ${clientName},</p>
              ${customBody}
            </div>
            <div style="padding: 20px; text-align: center; font-size: 12px; color: #666;">
              <p>PTC Financial | Tax & Financial Services</p>
              <p>If you have questions, please reply to this email or call us.</p>
            </div>
          </div>
        </div>`;
    }

    // Default tax season kickoff template
    return `
      <div style="font-family: 'Segoe UI', Arial, sans-serif; line-height: 1.6; color: #333;">
        <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="background: #0078d4; color: white; padding: 20px; text-align: center;">
            <h2 style="margin: 0;">Tax Season ${year}</h2>
            <p style="margin: 8px 0 0 0;">Let's Get Your ${defaultTaxYear} Taxes Done!</p>
          </div>
          <div style="padding: 20px; background: var(--tl-bg-subtle);">
            <p>Dear ${clientName},</p>

            <p>Happy New Year! Tax season is here, and we're ready to help you with your ${defaultTaxYear} tax return.</p>

            <div style="background: #e7f3ff; padding: 15px; border-left: 4px solid #0078d4; margin: 20px 0;">
              <strong>What's New This Year:</strong>
              <ul style="margin: 8px 0 0 0; padding-left: 20px;">
                <li>Updated standard deduction amounts</li>
                <li>Changes to tax brackets and rates</li>
                <li>New energy credits and incentives</li>
              </ul>
            </div>

            <p><strong>To get started, please gather the following documents:</strong></p>
            <ul style="padding-left: 20px;">
              <li>W-2s from all employers</li>
              <li>1099 forms (interest, dividends, freelance income)</li>
              <li>Mortgage interest statement (Form 1098)</li>
              <li>Property tax records</li>
              <li>Charitable donation receipts</li>
              <li>Healthcare coverage documents</li>
            </ul>

            <p>If you have any questions or would like to schedule an appointment, please reply to this email or give us a call.</p>

            <p>We look forward to working with you!</p>

            <p>Best regards,<br><strong>The PTC Financial Team</strong></p>
          </div>
          <div style="padding: 20px; text-align: center; font-size: 12px; color: #666;">
            <p>PTC Financial | Tax & Financial Services</p>
            <p>This email was sent to you because you are a valued client of PTC Financial.</p>
          </div>
        </div>
      </div>`;
  };

  // Send outreach mutation
  const sendMutation = useMutation({
    mutationFn: sendOutreach,
    onSuccess: (result) => {
      setSendResult({
        success: result.successCount,
        failed: result.failCount,
        errors: result.results.filter(r => !r.success).map(r => `${r.clientName}: ${r.error}`),
      });
      queryClient.invalidateQueries({ queryKey: ['eligibleClients'] });
      queryClient.invalidateQueries({ queryKey: ['outreachHistory'] });
      setSelectedClients(new Set());
    },
    onError: (error: Error) => {
      setSendResult({
        success: 0,
        failed: frozenSendIds.length,
        errors: [error.message],
      });
    },
  });

  // Select all eligible clients on the current page
  const handleSelectAllOnPage = () => {
    if (!currentPageClients.length) return;
    const eligibleOnPage = currentPageClients
      .filter(c => c.email && !c.alreadyContacted && !c.isOptedOut)
      .map(c => c.id);

    // Check if all eligible on page are already selected
    const allSelected = eligibleOnPage.every(id => selectedClients.has(id));

    if (allSelected) {
      // Deselect all on this page
      const newSelected = new Set(selectedClients);
      eligibleOnPage.forEach(id => newSelected.delete(id));
      setSelectedClients(newSelected);
    } else {
      // Select all eligible on this page
      const newSelected = new Set(selectedClients);
      eligibleOnPage.forEach(id => newSelected.add(id));
      setSelectedClients(newSelected);
    }
  };

  // Check if all eligible clients on current page are selected
  const allOnPageSelected = useMemo(() => {
    if (!currentPageClients.length) return false;
    const eligibleOnPage = currentPageClients.filter(c => c.email && !c.alreadyContacted && !c.isOptedOut);
    if (eligibleOnPage.length === 0) return false;
    return eligibleOnPage.every(c => selectedClients.has(c.id));
  }, [currentPageClients, selectedClients]);

  const eligibleOnPageCount = useMemo(() => {
    return currentPageClients.filter(c => c.email && !c.alreadyContacted && !c.isOptedOut).length;
  }, [currentPageClients]);

  const handleSelectClient = useCallback((clientId: string) => {
    setSelectedClients(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(clientId)) {
        newSelected.delete(clientId);
      } else {
        newSelected.add(clientId);
      }
      return newSelected;
    });
  }, []);

  const handleSendOutreach = () => {
    const template = templates?.find(tpl => tpl.id === selectedTemplate);

    // Step 6's binding rule: use the FROZEN set from when the preview opened, not a re-resolved
    // `selectedClients` -- this dialog can only be reached via the preview's own action button.
    sendMutation.mutate({
      clientIds: frozenSendIds,
      taxYear: defaultTaxYear,
      subject: customSubject || template?.subject.replace('{year}', (defaultTaxYear + 1).toString()),
      template: selectedTemplate,
      customBody: selectedTemplate === 'custom' ? customBody : undefined,
      includeDocumentChecklist: template?.includesDocumentChecklist,
      includeQuestionnaire: template?.includesQuestionnaire,
    });
  };

  // Wave 2B "Preparer by Year" -- undefined (the default) means "each client's own most recent
  // year"; a chosen year forces EVERY client to be evaluated against that SAME shared year (the
  // year picker). Two different response shapes (PreparerHistorySnapshot[] vs TaxReturn[], see
  // api.ts) are normalized into one lookup map here so the filter column/facet below never has to
  // care which query ran.
  const [preparerYearPicker, setPreparerYearPicker] = useState<number | undefined>(undefined);
  const { data: preparerSnapshotData } = useQuery<TaxReturn[] | PreparerHistorySnapshot[]>({
    queryKey: ['preparerByYear', preparerYearPicker],
    queryFn: () => (preparerYearPicker ? getPreparerForYear(preparerYearPicker) : getPreparerLastSnapshot()),
  });
  const lastPreparerMap = useMemo(() => {
    const map = new Map<string, { preparerId: string | null; preparerName: string | null }>();
    if (!preparerSnapshotData) return map;
    if (preparerYearPicker) {
      for (const r of preparerSnapshotData as TaxReturn[]) {
        map.set(r.clientId, { preparerId: r.preparerId || null, preparerName: r.preparerName || null });
      }
    } else {
      for (const h of preparerSnapshotData as PreparerHistorySnapshot[]) {
        map.set(h.clientId, { preparerId: h.lastPreparerId ?? null, preparerName: h.lastPreparerName ?? null });
      }
    }
    return map;
  }, [preparerSnapshotData, preparerYearPicker]);
  // The years offered in the picker: every year that actually appears on at least one loaded
  // client's own lastFiledYear, descending, so the control never offers a year with nothing to
  // show -- derived from data already fetched, no new endpoint.
  const preparerYearOptions = useMemo(() => {
    const years = new Set<number>();
    (clients || []).forEach((c) => { if (c.lastFiledYear) years.add(c.lastFiledYear); });
    return Array.from(years).sort((a, b) => b - a);
  }, [clients]);

  const lastPreparerFilterOptions = useMemo(() => {
    const names = new Set<string>();
    lastPreparerMap.forEach((v) => { if (v.preparerName) names.add(v.preparerName); });
    return Array.from(names).sort().map((name) => ({ value: name, label: name }));
  }, [lastPreparerMap]);

  // Every distinct preparer id+name seen on a client's preferred-preparer field -- feeds the
  // roster dialog's own preparer picker (FB-11's export helper needs a concrete preparerId+year,
  // not a name, to call GetPreparerByYearRoster).
  const rosterPreparerOptions = useMemo(() => {
    const byId = new Map<string, string>();
    (clients || []).forEach((c) => {
      if (c.preferredPreparerId && c.preferredPreparerName) byId.set(c.preferredPreparerId, c.preferredPreparerName);
    });
    return Array.from(byId.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [clients]);

  // Wave 2A: the fleet's shared filter engine (FilterColumn/ColumnHeaderFilter/applyClientFilters),
  // already 0-code-diff with ptc-core's shared modules per the fleet filter contract (§1.1) and
  // already used on TaxReturnsPage.tsx -- this is the first time it's wired onto THIS screen.
  const preparerFilterOptions = useMemo(() => {
    const names = new Set<string>();
    (clients || []).forEach((c) => { if (c.preferredPreparerName) names.add(c.preferredPreparerName); });
    return Array.from(names).sort().map((name) => ({ value: name, label: name }));
  }, [clients]);

  const filterColumns: FilterColumn<EligibleClient>[] = useMemo(() => [
    { key: 'name', label: t('outreachPage.table.columns.client'), filterType: 'text', getValue: (c) => c.name },
    {
      key: 'clientType',
      label: t('outreachPage.table.columns.type'),
      filterType: 'picklist',
      filterOptions: [
        { value: 'contact', label: t('outreachPage.filters.individual') },
        { value: 'account', label: t('outreachPage.filters.business') },
      ],
      getValue: (c) => c.clientType,
    },
    { key: 'lastFiledYear', label: t('outreachPage.table.columns.lastFiled'), filterType: 'number', getValue: (c) => c.lastFiledYear },
    { key: 'preferredPreparerName', label: t('outreachPage.table.columns.preparer'), filterType: 'picklist', filterOptions: preparerFilterOptions, getValue: (c) => c.preferredPreparerName },
    // Not rendered via ColumnHeaderFilter (no matching DataTable column) -- exists only so the
    // "my clients" quick view can filter by id, which is exact where matching by display name
    // could collide on two preparers sharing a name.
    { key: 'preferredPreparerId', label: t('outreachPage.table.columns.preparer'), filterType: 'text', getValue: (c) => c.preferredPreparerId },
    // Wave 2B: "last preparer" is a DIFFERENT fact from preferredPreparerName above -- the actual
    // preparer of the client's last (or year-picked) return, live-derived server-side with the
    // RULED tie-break, vs. the client's own stored preference. Own filterColumn/facet, never merged.
    { key: 'lastPreparerName', label: t('outreachPage.table.columns.lastPreparer'), filterType: 'picklist', filterOptions: lastPreparerFilterOptions, getValue: (c) => lastPreparerMap.get(c.id)?.preparerName ?? null },
    { key: 'alreadyContacted', label: t('outreachPage.filters.contacted'), filterType: 'boolean', getValue: (c) => c.alreadyContacted },
    { key: 'isInHouse', label: t('outreachPage.filters.inHouse'), filterType: 'boolean', getValue: (c) => c.isInHouse ?? false },
    { key: 'isOptedOut', label: t('outreachPage.filters.optedOut'), filterType: 'boolean', getValue: (c) => c.isOptedOut ?? false },
  ], [t, preparerFilterOptions, lastPreparerFilterOptions, lastPreparerMap]);

  const displayData = useMemo(
    () => applyClientFilters(clients || [], filters, filterColumns),
    [clients, filters, filterColumns]
  );

  // Wave 2A step 6: "select all N matching," not just the loaded page -- every eligible row in
  // the current FILTERED set (displayData), which this screen already has fully in memory (see
  // the plan doc's client-side-vs-server-side note: client-side is correct at this screen's scale,
  // and 10,000 rows is where that stops being true -- not relevant yet).
  const eligibleMatchingCount = useMemo(
    () => displayData.filter((c) => c.email && !c.alreadyContacted && !c.isOptedOut).length,
    [displayData]
  );
  const handleSelectAllMatching = useCallback(() => {
    const eligibleIds = displayData
      .filter((c) => c.email && !c.alreadyContacted && !c.isOptedOut)
      .map((c) => c.id);
    setSelectedClients(new Set(eligibleIds));
  }, [displayData]);

  const columnMenuLabels = useMemo(() => ({
    sortAZ: t('common.columnMenu.sortAZ'),
    sortZA: t('common.columnMenu.sortZA'),
    groupByHeader: t('common.columnMenu.groupBy'),
    clearGrouping: t('common.columnMenu.clearGrouping'),
    granularity: {
      year: t('common.columnMenu.granularity.year'),
      quarter: t('common.columnMenu.granularity.quarter'),
      month: t('common.columnMenu.granularity.month'),
      week: t('common.columnMenu.granularity.week'),
      day: t('common.columnMenu.granularity.day'),
    },
    triggerLabel: t('common.columnMenu.trigger'),
  }), [t]);

  const renderHeaderExtra = useCallback((key: string) => {
    const col = filterColumns.find((c) => c.key === key);
    // M1/C66: no column on this page supports grouping (no date-range column exists here), so
    // ColumnMenu is rendered with no `group` prop at all -- not a disabled item, an absent one.
    const menu = (OUTREACH_COLUMN_KEYS as readonly string[]).includes(key) ? (
      <ColumnMenu
        columnLabel={col?.label ?? key}
        onSort={(direction) => setSort({ col: key, dir: direction })}
        labels={columnMenuLabels}
      />
    ) : null;

    if (!col) return menu;
    return (
      <>
        {menu}
        <ColumnHeaderFilter
          column={col}
          value={filters}
          onChange={setFilters}
          getFacetCounts={() => ({
            ...computeFacetCounts(clients || [], filters, filterColumns, col),
            // C86: the lookup combobox's own "(has no value)" row count -- a SEPARATE function
            // (facetCounts.ts), merged in here under the engine's reserved sentinel key rather than
            // threading a whole new prop through ColumnHeaderFilter.
            [NO_VALUE_TOKEN]: computeNoValueCount(clients || [], filters, filterColumns, col),
          })}
        />
      </>
    );
  }, [filterColumns, filters, clients, setFilters, setSort, columnMenuLabels]);

  // Quick views: one-click presets over the same filter state (QuickViewPills, already built and
  // tested, reused verbatim from TaxReturnsPage's usage -- no new component).
  const quickViews: QuickView[] = useMemo(() => [
    { key: 'all', label: t('outreachPage.quickViews.all') },
    { key: 'my-clients', label: t('outreachPage.quickViews.myClients') },
    { key: 'not-contacted', label: t('outreachPage.quickViews.notContacted') },
    { key: 'in-house-pending', label: t('outreachPage.quickViews.inHousePending') },
    { key: 'filed-before-2025', label: t('outreachPage.quickViews.filedBefore2025') },
  ], [t]);
  const [activeQuickView, setActiveQuickView] = useState('all');
  const handleQuickView = useCallback((key: string) => {
    setActiveQuickView(key);
    switch (key) {
      case 'my-clients':
        setFilters(currentUserId ? { 'filter.preferredPreparerId.eq': currentUserId } : {});
        break;
      case 'not-contacted':
        setFilters({ 'filter.alreadyContacted.eq': 'false' });
        break;
      case 'in-house-pending':
        setFilters({ 'filter.isInHouse.eq': 'true', 'filter.alreadyContacted.eq': 'false' });
        break;
      case 'filed-before-2025':
        setFilters({ 'filter.lastFiledYear.lt': '2025' });
        break;
      default:
        setFilters({});
    }
  }, [currentUserId, setFilters]);

  const [rosterDialogOpen, setRosterDialogOpen] = useState(false);

  // Step 9's column chooser (see the module-scope comment above for scope: no multi-sort/group-by).
  const [visibleColKeys, setVisibleColKeys] = useState<string[]>(loadOutreachColumnOverrides);
  const toggleOutreachColumn = useCallback((colKey: string) => {
    setVisibleColKeys((prev) => {
      const next = prev.includes(colKey) ? prev.filter((k) => k !== colKey) : [...prev, colKey];
      try { localStorage.setItem(OUTREACH_COL_STORAGE_KEY, JSON.stringify(next)); } catch { /* best-effort */ }
      return next;
    });
  }, []);
  const outreachColumnLabels: Record<string, string> = useMemo(() => ({
    name: t('outreachPage.table.columns.client'),
    clientType: t('outreachPage.table.columns.type'),
    lastFiledYear: t('outreachPage.table.columns.lastFiled'),
    preferredPreparerName: t('outreachPage.table.columns.preparer'),
    lastPreparerName: t('outreachPage.table.columns.lastPreparer'),
    alreadyContacted: t('outreachPage.table.columns.status'),
  }), [t]);

  // Wave 3 (2026-11-02): saved views + the per-person landing default, moved off the localStorage
  // interim onto core's real stores (filter contract v3 §16.1-16.2) -- shared views now actually
  // work across devices/machines, not just "visible to the person who created it, on the device
  // they created it on". SCREEN_ID keys both stores; this screen has only one list view today, so
  // a single literal is correct (a second list view on this page would need its own id).
  const SCREEN_ID = 'outreach-clients';
  const { showToast } = useNotificationToast();
  const savedViewsQuery = useSavedViews(CRM_VIEWS_APP_ID, SCREEN_ID);
  const createView = useCreateSavedView();
  const deleteView = useDeleteSavedView();
  const setDefaultViewMutation = useSetSavedViewDefault();
  const savedViews = savedViewsQuery.data?.items ?? [];
  const defaultView = savedViews.find((v) => v.isDefault);
  const [saveViewDialogOpen, setSaveViewDialogOpen] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [newViewShared, setNewViewShared] = useState(false);

  const validFilterColumnKeys = useMemo(() => new Set(filterColumns.map((c) => c.key)), [filterColumns]);

  const applySavedView = useCallback((view: SavedViewDto) => {
    setActiveQuickView('all');
    const decoded = decodeQueryState(view.stateQuery);
    const { valid, staleCount } = splitStaleFilters(decoded.filters, validFilterColumnKeys);
    if (staleCount > 0) {
      showToast(
        t('outreachPage.savedViews.staleNoticeTitle'),
        t('outreachPage.savedViews.staleNoticeBody', { count: staleCount }),
        'warning'
      );
    }
    applyRemembered({ q: decoded.q, filters: valid });
  }, [applyRemembered, validFilterColumnKeys, showToast, t]);

  const handleSaveCurrentView = useCallback(() => {
    if (!newViewName.trim()) return;
    createView.mutate(
      {
        app: CRM_VIEWS_APP_ID,
        screenKey: SCREEN_ID,
        name: newViewName.trim(),
        stateQuery: encodeQueryState({ q: searchText || undefined, filters }),
        visibility: newViewShared ? 'team' : 'personal',
      },
      {
        onSuccess: () => {
          setSaveViewDialogOpen(false);
          setNewViewName('');
          setNewViewShared(false);
        },
        onError: () => showToast(t('outreachPage.savedViews.saveFailedTitle'), t('outreachPage.savedViews.saveFailedBody'), 'error'),
      }
    );
  }, [newViewName, newViewShared, searchText, filters, createView, showToast, t]);

  const handleSetDefaultView = useCallback((view: SavedViewDto) => {
    setDefaultViewMutation.mutate({ app: CRM_VIEWS_APP_ID, screenKey: SCREEN_ID, savedViewId: view.isDefault ? null : view.id });
  }, [setDefaultViewMutation]);

  const handleDeleteSavedView = useCallback((id: string) => {
    deleteView.mutate(id);
  }, [deleteView]);

  // The landing default + remember-on-change, both handled by one hook now (filter contract v3
  // §16.1): `q` and `filter.*` are this screen's list params; `filter.preferredPreparerId.eq`
  // scoped to the CALLER's own id is never remembered (core's binding rule -- the "my clients"
  // quick view sets exactly that pair). A saved default view, when set, wins over the ad-hoc
  // remembered state (the `prefer` option) -- neither applies before currentUserId resolves or the
  // saved-views list has loaded.
  const isOutreachListParam = useCallback((key: string) => key === 'q' || key.startsWith('filter.'), []);
  const isOutreachCallerScoped = useCallback(
    (key: string, value: string) => key === 'filter.preferredPreparerId.eq' && value === currentUserId,
    [currentUserId]
  );
  const { reset: resetScreenState } = useScreenStateDefault({
    app: CRM_VIEWS_APP_ID,
    screen: SCREEN_ID,
    isListParam: isOutreachListParam,
    isCallerScoped: isOutreachCallerScoped,
    enabled: !!currentUserId,
    prefer: { ready: !savedViewsQuery.isLoading, query: defaultView?.stateQuery ?? null },
  });

  // Generalizing the StaffRolesPage render-loop lesson (ptc-core#386, see CLAUDE.md's "Test
  // Coverage" section): this table wires `onPageDataChange` -> `setCurrentPageClients`, so
  // `columns` must stay reference-stable across renders that don't actually change what a row
  // looks like, or an unmemoized array here feeds DataTable's own `columnConfigs`/`processedData`
  // memos a new reference every render, which re-fires that effect every render, which re-renders
  // this page, which rebuilds `columns` again -- forever. `handleSelectClient` above is likewise
  // `useCallback`'d with an empty dep array (functional setState, no closed-over `selectedClients`)
  // so it never forces this memo to recompute on its own.
  const clientColumns: Column<EligibleClient>[] = useMemo(() => [
    {
      key: 'select',
      header: '',
      width: '40px',
      render: (client) => (
        <Checkbox
          checked={selectedClients.has(client.id)}
          onChange={() => handleSelectClient(client.id)}
          disabled={!client.email || client.alreadyContacted || client.isOptedOut}
        />
      ),
    },
    {
      key: 'name',
      header: t('outreachPage.table.columns.client'),
      sortable: true,
      render: (client) => {
        const hasDrakeChecklist = clientDrakeMap.has(client.id);
        const drakeChecklist = clientDrakeMap.get(client.id);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {client.clientType === 'account' ? <BuildingRegular /> : <PersonRegular />}
            <div>
              {/* Part C polish (2026-11-01): flexWrap let the name+badges line wrap to a second
                  line whenever they didn't all fit, making that row taller than its neighbors --
                  one line per row, always; the name truncates (minWidth: 0 is what lets a flex
                  child shrink below its content size at all), the badges never do. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap', minWidth: 0 }}>
                <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{client.name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                {client.isInHouse && (
                  <Tooltip
                    content={client.inHouseServices?.length
                      ? t('outreachPage.table.inHouseServicesTooltip', { services: client.inHouseServices.join(', ') })
                      : t('outreachPage.table.inHouseRecurringTooltip')}
                    relationship="label"
                  >
                    <Badge
                      appearance="filled"
                      color="success"
                      size="small"
                      icon={<StarRegular />}
                      style={{ cursor: 'help' }}
                    >
                      {t('outreachPage.table.inHouseBadge')}
                    </Badge>
                  </Tooltip>
                )}
                {hasDrakeChecklist && (
                  <Tooltip
                    content={t('outreachPage.table.drakeTooltip', { count: drakeChecklist?.checklistItems?.length || 0 })}
                    relationship="label"
                  >
                    <Badge
                      appearance="tint"
                      color="brand"
                      size="small"
                      icon={<DocumentPdfRegular />}
                      style={{ cursor: 'help' }}
                    >
                      {t('outreachPage.table.drakeBadge')}
                    </Badge>
                  </Tooltip>
                )}
                {client.isOptedOut && (
                  <Tooltip
                    content={client.optOutReason || t('outreachPage.table.optedOutTooltipFallback')}
                    relationship="label"
                  >
                    <Badge
                      appearance="filled"
                      color="danger"
                      size="small"
                      icon={<DismissCircleRegular />}
                      style={{ cursor: 'help' }}
                    >
                      {t('outreachPage.table.optedOutBadge')}
                    </Badge>
                  </Tooltip>
                )}
                </div>
              </div>
              <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {client.email || t('outreachPage.table.noEmail')}
                {client.phone && (
                  <Tooltip content={t('outreachPage.table.phoneTooltip', { phone: client.phone })} relationship="label">
                    <Badge
                      appearance="outline"
                      color="success"
                      size="small"
                      icon={<PhoneRegular />}
                      style={{ cursor: 'help' }}
                    />
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'clientType',
      header: t('outreachPage.table.columns.type'),
      sortable: true,
      render: (client) => (
        <Badge appearance="outline" color={client.clientType === 'account' ? 'brand' : 'informative'}>
          {client.clientType === 'account' ? t('outreachPage.table.business') : t('outreachPage.table.individual')}
        </Badge>
      ),
    },
    {
      key: 'lastFiledYear',
      header: t('outreachPage.table.columns.lastFiled'),
      sortable: true,
      render: (client) => client.lastFiledYear || '-',
    },
    {
      key: 'preferredPreparerName',
      header: t('outreachPage.table.columns.preparer'),
      sortable: true,
      render: (client) => client.preferredPreparerName || '-',
    },
    {
      key: 'lastPreparerName',
      header: t('outreachPage.table.columns.lastPreparer'),
      sortable: true,
      render: (client) => lastPreparerMap.get(client.id)?.preparerName || '-',
    },
    {
      key: 'alreadyContacted',
      header: t('outreachPage.table.columns.status'),
      sortable: true,
      getValue: (client) => {
        if (client.isOptedOut) return 2;
        if (client.alreadyContacted) return 1;
        return 0;
      },
      render: (client) => {
        if (client.isOptedOut) {
          return <Badge appearance="filled" color="danger">{t('outreachPage.table.optedOutBadge')}</Badge>;
        }
        if (!client.email) {
          return <Badge appearance="outline" color="warning">{t('outreachPage.table.noEmailBadge')}</Badge>;
        }
        if (client.alreadyContacted) {
          return <Badge appearance="filled" color="success">{t('outreachPage.table.contactedBadge')}</Badge>;
        }
        return <Badge appearance="outline" color="informative">{t('outreachPage.table.pendingBadge')}</Badge>;
      },
    },
    ...(isDataverseConfigured() ? [{
      key: 'actions',
      header: '',
      width: '120px',
      render: (client: EligibleClient) => (
        <Tooltip content={t('outreachPage.table.openDataverseTooltip')} relationship="label">
          <Button
            as="a"
            appearance="subtle"
            icon={<OpenRegular />}
            size="small"
            href={getDataverseRecordUrl(
              client.clientType === 'account' ? 'account' : 'contact',
              client.id
            ) ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {t('outreachPage.table.dataverseButton')}
          </Button>
        </Tooltip>
      ),
    }] : []),
  ], [t, selectedClients, handleSelectClient, clientDrakeMap, lastPreparerMap]);

  const historyColumns: Column<ClientOutreach>[] = [
    {
      key: 'clientName',
      header: t('outreachPage.table.columns.client'),
      sortable: true,
      render: (outreach) => (
        <div>
          <div style={{ fontWeight: 500 }}>{outreach.clientName}</div>
          <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)' }}>{outreach.clientEmail}</div>
        </div>
      ),
    },
    {
      key: 'subject',
      header: t('outreachPage.table.columns.subject'),
      sortable: true,
    },
    {
      key: 'sentAt',
      header: t('outreachPage.table.columns.sent'),
      sortable: true,
      getValue: (outreach) => new Date(outreach.sentAt).getTime(),
      render: (outreach) => format(new Date(outreach.sentAt), 'MMM d, yyyy h:mm a'),
    },
    {
      key: 'sentByName',
      header: t('outreachPage.table.columns.sentBy'),
      sortable: true,
      render: (outreach) => outreach.sentByName || '-',
    },
    {
      key: 'emailOpened',
      header: t('outreachPage.table.columns.opened'),
      sortable: true,
      getValue: (outreach) => outreach.emailOpened ? 1 : 0,
      render: (outreach) => (
        outreach.emailOpened ? (
          <Tooltip
            content={outreach.emailOpenedAt
              ? t('outreachPage.table.openedTooltip', { date: format(new Date(outreach.emailOpenedAt), 'MMM d, h:mm a') })
              : t('outreachPage.table.openedTooltipUnknown')}
            relationship="label"
          >
            <Badge appearance="filled" color="success" icon={<CheckmarkCircleRegular />}>
              {t('outreachPage.table.yes')}
            </Badge>
          </Tooltip>
        ) : (
          <Badge appearance="outline" color="subtle">{t('outreachPage.table.no')}</Badge>
        )
      ),
    },
    {
      key: 'linkClicked',
      header: t('outreachPage.table.columns.clicked'),
      sortable: true,
      getValue: (outreach) => outreach.linkClicked ? 1 : 0,
      render: (outreach) => (
        outreach.linkClicked ? (
          <Tooltip
            content={outreach.linkClickedAt
              ? t('outreachPage.table.clickedTooltip', { date: format(new Date(outreach.linkClickedAt), 'MMM d, h:mm a') })
              : t('outreachPage.table.clickedTooltipUnknown')}
            relationship="label"
          >
            <Badge appearance="filled" color="brand" icon={<CheckmarkCircleRegular />}>
              {t('outreachPage.table.yes')}
            </Badge>
          </Tooltip>
        ) : (
          <Badge appearance="outline" color="subtle">{t('outreachPage.table.no')}</Badge>
        )
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '80px',
      render: (outreach) => (
        <Button
          size="small"
          appearance="subtle"
          icon={<EyeRegular />}
          onClick={() => {
            setSelectedOutreach(outreach);
            setHistoryPreviewOpen(true);
          }}
        >
          {t('outreachPage.table.view')}
        </Button>
      ),
    },
  ];

  const eligibleCount = clients?.filter(c => c.email && !c.alreadyContacted && !c.isOptedOut).length || 0;
  const contactedCount = clients?.filter(c => c.alreadyContacted).length || 0;
  const optedOutCount = clients?.filter(c => c.isOptedOut).length || 0;
  const inHouseCount = clients?.filter(c => c.isInHouse === true).length || 0;
  const inHouseNotContactedCount = clients?.filter(c => c.isInHouse === true && c.email && !c.alreadyContacted && !c.isOptedOut).length || 0;
  const clientsWithPhoneCount = clients?.filter(c => c.phone).length || 0;

  if (clientsLoading) {
    return <TableSkeleton />;
  }

  if (clientsError) {
    return (
      <div className="error-container">
        <p>{t('outreachPage.errors.loadClientsFailed', { message: (clientsError as Error).message })}</p>
        <Button onClick={() => refetchClients()}>{t('outreachPage.errors.retry')}</Button>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2>{t('outreachPage.header.title')}</h2>
        <p>{t('outreachPage.header.subtitle')}</p>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom: '24px' }}>
        <div className="stat-card">
          <span className="stat-value" style={{ color: '#0078d4' }}>{clients?.length || 0}</span>
          <span className="stat-label">{t('outreachPage.stats.totalClients')}</span>
        </div>
        <div className="stat-card">
          <span className="stat-value" style={{ color: '#107c10' }}>{inHouseCount}</span>
          <span className="stat-label">{t('outreachPage.stats.inHouseClients')}</span>
          {inHouseNotContactedCount > 0 && (
            <span style={{ fontSize: tokens.fontSizeBase200, color: '#d83b01' }}>
              {t('outreachPage.stats.inHousePending', { count: inHouseNotContactedCount })}
            </span>
          )}
        </div>
        <div className="stat-card">
          <span className="stat-value" style={{ color: '#107c10' }}>{eligibleCount}</span>
          <span className="stat-label">{t('outreachPage.stats.readyForOutreach')}</span>
        </div>
        <div className="stat-card">
          <span className="stat-value" style={{ color: '#8764b8' }}>{contactedCount}</span>
          <span className="stat-label">{t('outreachPage.stats.alreadyContacted')}</span>
        </div>
        <div className="stat-card">
          <span className="stat-value" style={{ color: '#EF4444' }}>{optedOutCount}</span>
          <span className="stat-label">{t('outreachPage.stats.optedOut')}</span>
        </div>
        <div className="stat-card">
          <span className="stat-value" style={{ color: '#d83b01' }}>{clientDrakeMap.size}</span>
          <span className="stat-label">{t('outreachPage.stats.withDrakeChecklist')}</span>
        </div>
        <div className="stat-card">
          <span className="stat-value" style={{ color: '#107c10' }}>{clientsWithPhoneCount}</span>
          <span className="stat-label">{t('outreachPage.stats.withPhone')}</span>
        </div>
        <div className="stat-card">
          <span className="stat-value" style={{ color: '#0078d4' }}>{selectedClients.size}</span>
          <span className="stat-label">{t('outreachPage.stats.selected')}</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="toolbar" style={{ marginBottom: '16px' }}>
        <Button
          appearance="subtle"
          onClick={handleSelectAllOnPage}
          disabled={eligibleOnPageCount === 0}
        >
          {allOnPageSelected
            ? t('outreachPage.toolbar.deselectPage')
            : t('outreachPage.toolbar.selectAllOnPage', { count: eligibleOnPageCount })}
        </Button>
        <Button
          appearance="subtle"
          onClick={handleSelectAllMatching}
          disabled={eligibleMatchingCount === 0}
        >
          {t('outreachPage.toolbar.selectAllMatching', { count: eligibleMatchingCount })}
        </Button>
        <Button
          appearance="subtle"
          onClick={() => setSelectedClients(new Set())}
          disabled={selectedClients.size === 0}
        >
          {t('outreachPage.toolbar.clearSelection', { count: selectedClients.size })}
        </Button>

        {/* Preview Button -- step 6's required-preview gate: ALL THREE entry points below (this
            standalone trigger, Email Only, Send Email+SMS) open this same dialog, which freezes
            the id set on open via openPreviewFor; only its own action buttons advance to an actual
            send, never a trigger that skips this dialog. */}
        <Dialog open={previewDialogOpen} onOpenChange={(_, data) => {
          if (data.open) openPreviewFor(null);
          else setPreviewDialogOpen(false);
        }}>
          <DialogTrigger disableButtonEnhancement>
            <Button
              appearance="secondary"
              icon={<EyeRegular />}
              disabled={selectedClients.size === 0}
            >
              {t('outreachPage.toolbar.preview', { count: selectedClients.size })}
            </Button>
          </DialogTrigger>
          <DialogSurface style={{ maxWidth: '700px', maxHeight: '90vh' }}>
            <DialogBody>
              <DialogTitle>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>{t('outreachPage.previewDialog.title')}</span>
                  {frozenClientsList.length > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Button
                        appearance="subtle"
                        icon={<ChevronLeftRegular />}
                        disabled={previewIndex === 0}
                        onClick={() => setPreviewIndex(i => Math.max(0, i - 1))}
                      />
                      <span style={{ fontSize: tokens.fontSizeBase300, fontWeight: 400 }}>
                        {t('outreachPage.previewDialog.pagination', { current: previewIndex + 1, total: frozenClientsList.length })}
                      </span>
                      <Button
                        appearance="subtle"
                        icon={<ChevronRightRegular />}
                        disabled={previewIndex === frozenClientsList.length - 1}
                        onClick={() => setPreviewIndex(i => Math.min(frozenClientsList.length - 1, i + 1))}
                      />
                    </div>
                  )}
                </div>
              </DialogTitle>
              <DialogContent>
                {currentPreviewClient && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Client info */}
                    <div style={{ padding: '12px', backgroundColor: 'var(--tl-bg-subtle)', borderRadius: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        {currentPreviewClient.clientType === 'account' ? <BuildingRegular /> : <PersonRegular />}
                        <strong>{currentPreviewClient.name}</strong>
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>
                        {t('outreachPage.previewDialog.to', { email: currentPreviewClient.email })}
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>
                        {/* Subject fallback 'Tax Season - Let's Get Started!' is client-facing email
                            subject content (not UI chrome) -- left as a plain literal, same as the
                            email body generated below. See report. */}
                        {t('outreachPage.previewDialog.subject', {
                          subject: customSubject || templates?.find(tpl => tpl.id === selectedTemplate)?.subject.replace('{year}', (defaultTaxYear + 1).toString()) || 'Tax Season - Let\'s Get Started!',
                        })}
                      </div>
                    </div>

                    {/* Email preview */}
                    <div style={{
                      border: '1px solid var(--tl-border)',
                      borderRadius: '4px',
                      maxHeight: '400px',
                      overflow: 'auto',
                      backgroundColor: '#fff',
                    }}>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(generatePreviewEmail(currentPreviewClient.name), {
                            ALLOWED_TAGS: ['div', 'span', 'p', 'h1', 'h2', 'h3', 'strong', 'em', 'ul', 'ol', 'li', 'br', 'a', 'table', 'tr', 'td', 'th', 'tbody', 'thead'],
                            ALLOWED_ATTR: ['style', 'href', 'class']
                          })
                        }}
                      />
                    </div>
                  </div>
                )}
              </DialogContent>
              <DialogActions>
                <DialogTrigger disableButtonEnhancement>
                  <Button appearance="secondary">{t('common.close')}</Button>
                </DialogTrigger>
                {pendingSendPath === 'emailSms' ? (
                  <Button
                    appearance="primary"
                    icon={<SendRegular />}
                    onClick={() => {
                      setPreviewDialogOpen(false);
                      setEnhancedSendDialogOpen(true);
                    }}
                  >
                    {t('outreachPage.previewDialog.continueToEmailSms')}
                  </Button>
                ) : (
                  <Button
                    appearance="primary"
                    icon={<SendRegular />}
                    onClick={() => {
                      setPreviewDialogOpen(false);
                      setSendDialogOpen(true);
                    }}
                  >
                    {t('outreachPage.previewDialog.sendToAllSelected')}
                  </Button>
                )}
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>

        <Button
          appearance="secondary"
          icon={<SendRegular />}
          disabled={selectedClients.size === 0}
          onClick={() => openPreviewFor('emailSms')}
        >
          {t('outreachPage.toolbar.sendEmailSms')}
        </Button>

        <Button
          appearance="primary"
          icon={<MailRegular />}
          disabled={selectedClients.size === 0}
          onClick={() => openPreviewFor('email')}
        >
          {t('outreachPage.toolbar.emailOnly', { count: selectedClients.size })}
        </Button>

        {/* Send Dialog -- no trigger of its own; only the preview dialog's actions above open it,
            so a send always goes through the required-preview gate first (step 6). */}
        <Dialog open={sendDialogOpen} onOpenChange={(_, data) => {
          setSendDialogOpen(data.open);
          if (!data.open) setSendResult(null);
        }}>
          <DialogSurface style={{ maxWidth: '600px' }}>
            <DialogBody>
              <DialogTitle>{t('outreachPage.sendDialog.title')}</DialogTitle>
              <DialogContent>
                {sendResult ? (
                  <div>
                    <div style={{
                      padding: '16px',
                      marginBottom: '16px',
                      backgroundColor: sendResult.failed === 0 ? '#dff6dd' : '#fde7e9',
                      borderRadius: '4px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        {sendResult.failed === 0 ? (
                          <CheckmarkCircleRegular style={{ color: '#107c10' }} />
                        ) : (
                          <DismissCircleRegular style={{ color: '#a4262c' }} />
                        )}
                        <strong>
                          {t('outreachPage.sendDialog.result.successSummary', { count: sendResult.success })}
                          {sendResult.failed > 0 && t('outreachPage.sendDialog.result.failedSuffix', { count: sendResult.failed })}
                        </strong>
                      </div>
                      {sendResult.errors.length > 0 && (
                        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px' }}>
                          {sendResult.errors.slice(0, 5).map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                          {sendResult.errors.length > 5 && (
                            <li>{t('outreachPage.sendDialog.result.moreErrors', { count: sendResult.errors.length - 5 })}</li>
                          )}
                        </ul>
                      )}
                    </div>
                    <Button appearance="primary" onClick={() => setSendDialogOpen(false)}>
                      {t('common.close')}
                    </Button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                        {t('outreachPage.sendDialog.templateLabel')}
                      </label>
                      <Dropdown
                        value={selectedTemplate}
                        onOptionSelect={(_, data) => setSelectedTemplate(data.optionValue as string)}
                        style={{ width: '100%' }}
                      >
                        {templates?.map(tpl => (
                          // tpl.name / tpl.description come from the EmailTemplate API response --
                          // server-provided template metadata, not UI chrome, so left untranslated.
                          <Option key={tpl.id} value={tpl.id} text={tpl.name}>
                            <div>
                              <div>{tpl.name}</div>
                              <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)' }}>{tpl.description}</div>
                            </div>
                          </Option>
                        ))}
                      </Dropdown>
                    </div>

                    {selectedTemplate === 'custom' && (
                      <>
                        <div>
                          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                            {t('outreachPage.sendDialog.subjectLabel')}
                          </label>
                          <Input
                            value={customSubject}
                            onChange={(_, data) => setCustomSubject(data.value)}
                            placeholder={t('outreachPage.sendDialog.subjectPlaceholder')}
                            style={{ width: '100%' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                            {t('outreachPage.sendDialog.bodyLabel')}
                          </label>
                          <Textarea
                            value={customBody}
                            onChange={(_, data) => setCustomBody(data.value)}
                            placeholder={t('outreachPage.sendDialog.bodyPlaceholder')}
                            rows={8}
                            style={{ width: '100%' }}
                          />
                        </div>
                      </>
                    )}

                    <div style={{ padding: '12px', backgroundColor: 'var(--tl-bg-subtle)', borderRadius: '4px' }}>
                      <div style={{ fontWeight: 500, marginBottom: '8px' }}>
                        <MailRegular style={{ marginRight: '8px' }} />
                        {t('outreachPage.sendDialog.readyToSend', { count: selectedClients.size })}
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>
                        {t('outreachPage.sendDialog.sendNote')}
                      </div>
                    </div>
                  </div>
                )}
              </DialogContent>
              {!sendResult && (
                <DialogActions>
                  <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary">{t('common.cancel')}</Button>
                  </DialogTrigger>
                  <Button
                    appearance="primary"
                    icon={<SendRegular />}
                    onClick={handleSendOutreach}
                    disabled={sendMutation.isPending || (selectedTemplate === 'custom' && !customBody)}
                  >
                    {sendMutation.isPending ? t('outreachPage.sendDialog.sending') : t('outreachPage.sendDialog.sendEmails')}
                  </Button>
                </DialogActions>
              )}
            </DialogBody>
          </DialogSurface>
        </Dialog>
      </div>

      {/* Tabs */}
      <TabList
        selectedValue={selectedTab}
        onTabSelect={(_, data) => setSelectedTab(data.value as string)}
        style={{ marginBottom: '16px' }}
      >
        <Tab value="clients" icon={<PersonRegular />}>
          {t('outreachPage.tabs.eligibleClients', { count: clients?.length || 0 })}
        </Tab>
        <Tab value="history" icon={<HistoryRegular />}>
          {t('outreachPage.tabs.outreachHistory', { count: history?.length || 0 })}
        </Tab>
      </TabList>

      {/* Content */}
      <div className="card">
        {selectedTab === 'clients' ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
              {/* §16.5: this page owns the search box (controlled, via searchText/onSearchTextChange
                  below) instead of DataTable's own uncontrolled one, so `q` can live in the URL and
                  `/` can focus it from anywhere on the page. */}
              <Input
                ref={searchInputRef}
                value={searchText}
                onChange={(_, data) => setSearchText(data.value)}
                placeholder={t('outreachPage.table.searchClients')}
                contentBefore={<SearchRegular />}
                style={{ minWidth: '240px' }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)' }}>
                  {t('outreachPage.filters.lastPreparerYear')}
                </span>
                <Dropdown
                  style={{ minWidth: '140px' }}
                  placeholder={t('outreachPage.filters.lastPreparerYearLatest')}
                  value={preparerYearPicker ? String(preparerYearPicker) : t('outreachPage.filters.lastPreparerYearLatest')}
                  selectedOptions={preparerYearPicker ? [String(preparerYearPicker)] : ['']}
                  onOptionSelect={(_, data) => setPreparerYearPicker(data.optionValue ? Number(data.optionValue) : undefined)}
                >
                  <Option value="" text={t('outreachPage.filters.lastPreparerYearLatest')}>
                    {t('outreachPage.filters.lastPreparerYearLatest')}
                  </Option>
                  {preparerYearOptions.map((y) => (
                    <Option key={y} value={String(y)} text={String(y)}>{y}</Option>
                  ))}
                </Dropdown>
              </div>
              <Button appearance="secondary" onClick={() => setRosterDialogOpen(true)} disabled={rosterPreparerOptions.length === 0}>
                {t('outreachPage.rosterDialog.openButton')}
              </Button>

              {/* Wave 2A step 7: saved views -- a named filter+search combo a staff member
                  created, one of which may be their own default (step 8). */}
              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <Button appearance="subtle" icon={<BookmarkRegular />}>
                    {t('outreachPage.savedViews.menuButton')}
                  </Button>
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    {savedViews.length === 0 && (
                      <MenuItem disabled>{t('outreachPage.savedViews.none')}</MenuItem>
                    )}
                    {savedViews.map((view) => (
                      <MenuItem
                        key={view.id}
                        icon={view.isDefault ? <StarFilled /> : undefined}
                        onClick={() => applySavedView(view)}
                        secondaryContent={
                          <span style={{ display: 'inline-flex', gap: '4px' }}>
                            <Button
                              appearance="transparent"
                              size="small"
                              icon={view.isDefault ? <StarFilled /> : <StarRegular />}
                              title={t('outreachPage.savedViews.setDefault')}
                              onClick={(e) => { e.stopPropagation(); handleSetDefaultView(view); }}
                            />
                            {view.canEdit && (
                              <Button
                                appearance="transparent"
                                size="small"
                                icon={<DeleteRegular />}
                                title={t('outreachPage.savedViews.delete')}
                                onClick={(e) => { e.stopPropagation(); handleDeleteSavedView(view.id); }}
                              />
                            )}
                          </span>
                        }
                      >
                        {/* A team view someone ELSE owns carries their name, so opening it never
                            reads as yours (core's visibility rule). */}
                        {view.visibility === 'team' && !view.isOwner && view.ownerName
                          ? `${view.name} — ${view.ownerName}`
                          : view.name}
                      </MenuItem>
                    ))}
                    <MenuDivider />
                    <MenuItem icon={<SaveRegular />} onClick={() => setSaveViewDialogOpen(true)}>
                      {t('outreachPage.savedViews.saveCurrent')}
                    </MenuItem>
                    <MenuItem onClick={resetScreenState}>
                      {t('outreachPage.savedViews.resetToDefault')}
                    </MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>

              {/* Step 9: column chooser, ported from TaxReturnsPage.tsx's own pattern. */}
              <Popover>
                <PopoverTrigger disableButtonEnhancement>
                  <Tooltip content={t('outreachPage.toggleColumns')} relationship="label">
                    <Button appearance="subtle" icon={<ColumnTripleRegular />}>
                      <Badge appearance="filled" size="small" style={{ marginLeft: '4px' }}>
                        {visibleColKeys.length}/{OUTREACH_COLUMN_KEYS.length}
                      </Badge>
                    </Button>
                  </Tooltip>
                </PopoverTrigger>
                <PopoverSurface style={{ padding: '12px', minWidth: '200px' }}>
                  <div style={{ fontWeight: 600, marginBottom: '8px', fontSize: '13px' }}>{t('outreachPage.visibleColumns')}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {OUTREACH_COLUMN_KEYS.map((colKey) => (
                      <Checkbox
                        key={colKey}
                        checked={visibleColKeys.includes(colKey)}
                        onChange={() => toggleOutreachColumn(colKey)}
                        label={outreachColumnLabels[colKey]}
                      />
                    ))}
                  </div>
                </PopoverSurface>
              </Popover>
            </div>

            <Dialog open={saveViewDialogOpen} onOpenChange={(_, data) => setSaveViewDialogOpen(data.open)}>
              <DialogSurface>
                <DialogBody>
                  <DialogTitle>{t('outreachPage.savedViews.saveDialogTitle')}</DialogTitle>
                  <DialogContent>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <Input
                        value={newViewName}
                        onChange={(_, data) => setNewViewName(data.value)}
                        placeholder={t('outreachPage.savedViews.namePlaceholder')}
                      />
                      <Checkbox
                        checked={newViewShared}
                        onChange={(_, data) => setNewViewShared(!!data.checked)}
                        label={t('outreachPage.savedViews.shareToTeam')}
                      />
                    </div>
                  </DialogContent>
                  <DialogActions>
                    <Button appearance="secondary" onClick={() => setSaveViewDialogOpen(false)}>{t('common.cancel')}</Button>
                    <Button appearance="primary" disabled={!newViewName.trim()} onClick={handleSaveCurrentView}>
                      {t('outreachPage.savedViews.save')}
                    </Button>
                  </DialogActions>
                </DialogBody>
              </DialogSurface>
            </Dialog>

            <QuickViewPills views={quickViews} activeKey={activeQuickView} onSelect={handleQuickView} />
            <ActiveFiltersChips columns={filterColumns} value={filters} onChange={setFilters} />
            <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)', margin: '4px 0 8px' }}>
              {t('outreachPage.filters.resultCount', { shown: displayData.length, total: clients?.length || 0 })}
            </div>
            <DataTable
              data={displayData}
              columns={clientColumns}
              rowKey="id"
              searchable
              searchText={searchText}
              onSearchTextChange={setSearchText}
              searchKeys={OUTREACH_CLIENT_SEARCH_KEYS}
              sortColumn={sort?.col}
              sortDirection={sort?.dir}
              onSortChange={(next) => setSort(next ? { col: String(next.column), dir: next.direction } : null)}
              emptyMessage={t('outreachPage.table.emptyClients')}
              paginated
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              onPageDataChange={(items) => setCurrentPageClients(items as EligibleClient[])}
              renderHeaderExtra={renderHeaderExtra}
              visibleColumns={['select', ...visibleColKeys, ...(isDataverseConfigured() ? ['actions'] : [])]}
            />
          </>
        ) : (
          historyLoading ? (
            <div className="loading-container">
              <Spinner size="medium" label={t('outreachPage.loading.history')} />
            </div>
          ) : historyError ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <DismissCircleRegular style={{ fontSize: '48px', color: '#a4262c', marginBottom: '16px' }} />
              <p style={{ color: '#a4262c', marginBottom: '16px' }}>
                {t('outreachPage.errors.loadHistoryFailed', { message: (historyError as Error).message })}
              </p>
              <Button
                appearance="primary"
                icon={<ArrowSyncRegular />}
                onClick={() => refetchHistory()}
              >
                {t('outreachPage.errors.retry')}
              </Button>
            </div>
          ) : (
            <DataTable
              data={history || []}
              columns={historyColumns}
              rowKey="id"
              searchable
              searchPlaceholder={t('outreachPage.table.searchHistory')}
              searchKeys={['clientName', 'clientEmail', 'subject']}
              emptyMessage={t('outreachPage.table.emptyHistory')}
              initialSort={{ column: 'sentAt', direction: 'desc' }}
              paginated
            />
          )
        )}
      </div>

      <PreparerRosterDialog
        open={rosterDialogOpen}
        onOpenChange={setRosterDialogOpen}
        preparerOptions={rosterPreparerOptions}
        years={preparerYearOptions.length > 0 ? preparerYearOptions : [defaultTaxYear]}
      />

      {/* History Email Preview Dialog */}
      <Dialog open={historyPreviewOpen} onOpenChange={(_, data) => setHistoryPreviewOpen(data.open)}>
        <DialogSurface style={{ maxWidth: '700px', maxHeight: '90vh' }}>
          <DialogBody>
            <DialogTitle>{t('outreachPage.previewDialog.title')}</DialogTitle>
            <DialogContent>
              {selectedOutreach && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* Email metadata -- clientName/clientEmail/subject/sentByName are the sent
                      outreach record's own data, not UI chrome; only the labels are translated. */}
                  <div style={{ padding: '12px', backgroundColor: 'var(--tl-bg-subtle)', borderRadius: '4px' }}>
                    <div>
                      <Trans
                        i18nKey="outreachPage.historyPreviewDialog.to"
                        values={{ name: selectedOutreach.clientName, email: selectedOutreach.clientEmail }}
                        components={{ strong: <strong /> }}
                      />
                    </div>
                    <div>
                      <Trans
                        i18nKey="outreachPage.historyPreviewDialog.subject"
                        values={{ subject: selectedOutreach.subject }}
                        components={{ strong: <strong /> }}
                      />
                    </div>
                    <div>
                      <Trans
                        i18nKey="outreachPage.historyPreviewDialog.sent"
                        values={{ date: format(new Date(selectedOutreach.sentAt), 'MMM d, yyyy h:mm a') }}
                        components={{ strong: <strong /> }}
                      />
                    </div>
                    <div>
                      <Trans
                        i18nKey="outreachPage.historyPreviewDialog.sentBy"
                        values={{ name: selectedOutreach.sentByName || t('outreachPage.historyPreviewDialog.sentBySystemFallback') }}
                        components={{ strong: <strong /> }}
                      />
                    </div>
                  </div>

                  {/* Email body preview */}
                  {selectedOutreach.emailBody ? (
                    <div style={{
                      border: '1px solid var(--tl-border)',
                      borderRadius: '4px',
                      maxHeight: '400px',
                      overflow: 'auto',
                      backgroundColor: '#fff',
                    }}>
                      <div dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(selectedOutreach.emailBody, {
                          ALLOWED_TAGS: ['div', 'span', 'p', 'h1', 'h2', 'h3', 'strong', 'em', 'ul', 'ol', 'li', 'br', 'a', 'table', 'tr', 'td', 'th', 'tbody', 'thead'],
                          ALLOWED_ATTR: ['style', 'href', 'class']
                        })
                      }} />
                    </div>
                  ) : (
                    <div style={{
                      padding: '24px',
                      textAlign: 'center',
                      color: '#666',
                      fontStyle: 'italic',
                      backgroundColor: 'var(--tl-bg-subtle)',
                      borderRadius: '4px',
                    }}>
                      {t('outreachPage.historyPreviewDialog.bodyUnavailable')}
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setHistoryPreviewOpen(false)}>
                {t('common.close')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Enhanced Send Outreach Dialog (Email + SMS) */}
      <SendOutreachDialog
        open={enhancedSendDialogOpen}
        onOpenChange={setEnhancedSendDialogOpen}
        selectedClients={frozenClientsList}
        taxYear={defaultTaxYear}
        onComplete={() => {
          queryClient.invalidateQueries({ queryKey: ['eligibleClients'] });
          queryClient.invalidateQueries({ queryKey: ['outreachHistory'] });
          setSelectedClients(new Set());
        }}
      />
    </div>
  );
}
